import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trpc } from "../lib/trpc.js";
import { ProgressUpdater } from "../features/progress/ProgressUpdater.js";
import { CommentList } from "../features/comments/CommentList.js";
import { Skeleton } from "../components/ui/Skeleton.js";
import { ClubProgressBar } from "../components/ui/ClubProgressBar.js";
import { NavActions } from "../components/NavActions.js";

export function ClubBookView() {
  const { clubId, clubBookId } = useParams<{ clubId: string; clubBookId: string }>();
  const { t } = useTranslation();

  const { data: clubBooks, isLoading: clubBooksLoading } = trpc.clubBooks.list.useQuery(
    { clubId: clubId! },
    { enabled: !!clubId }
  );
  const { data: progress, isLoading: progressLoading } = trpc.progress.list.useQuery(
    { clubBookId: clubBookId! },
    { enabled: !!clubBookId }
  );

  const clubBook = clubBooks?.find((cb) => cb.id === clubBookId);
  const myProgress = progress?.find((p) => p.isViewer);

  if (clubBooksLoading) {
    return (
      <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
        <header
          className="sticky top-0 z-10 flex items-center gap-3 px-6 py-4 md:px-10"
          style={{
            background: "oklch(97% 0.014 82 / 0.92)",
            backdropFilter: "blur(8px)",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <Skeleton style={{ width: "5rem", height: "1rem" }} />
          <span style={{ color: "var(--color-border-strong)" }}>|</span>
          <Skeleton style={{ width: "12rem", height: "1.1rem" }} />
        </header>
        <main className="mx-auto max-w-4xl px-6 py-10 grid grid-cols-1 gap-8 md:grid-cols-3 md:px-10">
          <section className="md:col-span-2 space-y-4">
            <Skeleton style={{ width: "6rem", height: "0.75rem" }} />
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center gap-3">
                  <Skeleton style={{ width: "2rem", height: "2rem", borderRadius: "50%" }} />
                  <Skeleton style={{ width: "8rem", height: "0.8rem" }} />
                </div>
                <Skeleton style={{ height: "3.5rem", marginLeft: "2.75rem" }} />
              </div>
            ))}
          </section>
          <section className="space-y-4">
            <Skeleton style={{ width: "6rem", height: "0.75rem" }} />
            <Skeleton style={{ height: "6rem", borderRadius: "0.75rem" }} />
            <Skeleton style={{ height: "8rem", borderRadius: "0.75rem" }} />
          </section>
        </main>
      </div>
    );
  }

  if (!clubBook) {
    return (
      <div
        className="flex min-h-screen items-center justify-center gap-2"
        style={{ background: "var(--color-background)" }}
      >
        <span className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
          {t("common.notFound")}
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-10 flex items-center gap-3 px-6 py-4 md:px-10"
        style={{
          background: "oklch(97% 0.014 82 / 0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <Link
          to={`/clubs/${clubId}`}
          className="text-sm flex-shrink-0"
          style={{ color: "var(--color-muted-foreground)", textDecoration: "none" }}
        >
          {t("nav.backToClub")}
        </Link>
        <span style={{ color: "var(--color-border-strong)" }}>|</span>
        <div className="min-w-0 flex items-baseline gap-2">
          <span
            className="font-serif font-semibold truncate"
            style={{ color: "var(--color-foreground)" }}
          >
            {clubBook.book.title}
          </span>
          {clubBook.book.authors.length > 0 && (
            <span
              className="text-sm hidden sm:inline truncate"
              style={{ color: "var(--color-muted-foreground)" }}
            >
              {clubBook.book.authors.join(", ")}
            </span>
          )}
        </div>
        <div className="ml-auto"><NavActions /></div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10 grid grid-cols-1 gap-8 md:grid-cols-3 md:px-10">
        {/* Discussion */}
        <section
          className="md:col-span-2 space-y-6"
          style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both" }}
        >
          <div>
            <p
              className="text-xs font-medium uppercase tracking-widest mb-4"
              style={{ color: "var(--color-accent)" }}
            >
              {t("clubBook.discussion")}
            </p>
            {clubBook.state === "BUCKET" ? (
              <div
                className="rounded-xl px-5 py-8 text-center"
                style={{
                  background: "var(--color-card)",
                  border: "1px dashed var(--color-border-strong)",
                }}
              >
                <p className="font-serif italic" style={{ color: "var(--color-muted-foreground)" }}>
                  {t("clubBook.lockedComments")}
                </p>
              </div>
            ) : (
              <CommentList
                clubBookId={clubBookId!}
                myProgress={myProgress?.page}
                totalPages={clubBook.canonicalPageCount}
              />
            )}
          </div>
        </section>

        {/* Progress sidebar */}
        <section
          className="space-y-5"
          style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both" }}
        >
          {/* Your progress */}
          {clubBook.state === "CURRENT" && (
            <div
              className="rounded-xl p-4"
              style={{
                background: "var(--color-card)",
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-sm)",
              }}
            >
              <p
                className="text-xs font-medium uppercase tracking-widest mb-3"
                style={{ color: "var(--color-accent)" }}
              >
                {t("clubBook.yourProgress")}
              </p>
              <ProgressUpdater
                clubBookId={clubBookId!}
                currentPage={myProgress?.page ?? null}
                currentTotalPages={myProgress?.totalPages ?? null}
                canonicalPageCount={clubBook.canonicalPageCount}
              />
              {myProgress && (
                <p className="mt-2 text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                  {(myProgress.totalPages ?? clubBook.canonicalPageCount)
                    ? t("clubBook.pageOf", { page: myProgress.page, max: myProgress.totalPages ?? clubBook.canonicalPageCount })
                    : `Page ${myProgress.page}`}
                </p>
              )}
            </div>
          )}

          {/* Club progress */}
          {progressLoading && (
            <div
              className="rounded-xl p-4"
              style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}
            >
              <Skeleton style={{ width: "6rem", height: "0.75rem", marginBottom: "1rem" }} />
              {[...Array(2)].map((_, i) => (
                <div key={i} className="space-y-1.5 mb-3">
                  <div className="flex justify-between">
                    <Skeleton style={{ width: "6rem", height: "0.75rem" }} />
                    <Skeleton style={{ width: "3rem", height: "0.75rem" }} />
                  </div>
                  <Skeleton style={{ height: "0.375rem", borderRadius: "9999px" }} />
                </div>
              ))}
            </div>
          )}
          {!progressLoading && progress && progress.length > 0 && (
            <div
              className="rounded-xl p-4"
              style={{
                background: "var(--color-card)",
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-sm)",
              }}
            >
              <p
                className="text-xs font-medium uppercase tracking-widest mb-4"
                style={{ color: "var(--color-accent)" }}
              >
                {t("clubBook.clubProgress")}
              </p>
              <ClubProgressBar
                members={progress}
                totalPages={clubBook.canonicalPageCount}
                size="default"
              />
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
